import type { QueryClient } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';
import { queryKeys } from '@/api/queryKeys';
import type { ApiErrorBody, NextcloudSaveEvent } from '@/api/types';
import { formatBytes } from '@/utils/format';

/**
 * *Save to Nextcloud* for received attachments (ADR 0086): the saves this
 * window has asked for, and what became of them.
 *
 * A module-level store rather than component state, on purpose. The reading
 * pane's attachment list is where a save is started and where each row shows
 * how its file is doing, but the list unmounts the moment the person opens the
 * next message -- and a save the server is halfway through must neither be
 * cancelled by that nor lose its result. The snackbar (`SaveSnackbar`) and
 * every list read from here; nothing here renders.
 *
 * One save runs at a time; a second one waits its turn (`queued`). The server
 * would take two per person, but one stream at a time keeps the snackbar's one
 * sentence true and costs nothing a person would notice.
 *
 * The bytes never pass through here: the server reads each part from the
 * mailbox and writes it to Nextcloud itself. What arrives are events.
 */

export type SaveFileState = 'queued' | 'reading' | 'uploading' | 'saved' | 'failed' | 'cancelled';

export interface SaveFile {
  partId: string;
  /** The name the message gives the attachment. */
  filename: string;
  sizeBytes: number;
  state: SaveFileState;
  doneBytes: number;
  totalBytes: number;
  /** Once saved: its name on Nextcloud (`report (2).pdf` after a collision). */
  name?: string;
  path?: string;
  openUrl?: string;
  renamed?: boolean;
  /** Why it was not saved, in words. */
  problem?: string;
}

export interface SaveShare {
  state: 'working' | 'copied' | 'shown' | 'failed';
  url?: string;
  /** Answered once, when the instance enforces a password. Never stored anywhere else. */
  password?: string;
  problem?: string;
}

export interface SaveJob {
  id: number;
  /** The id the server knows this save by, for Cancel. */
  saveId: string;
  accountId: string;
  messageId: string;
  /** The message's subject, for "Files shared from mail" if a link is made. */
  subject: string;
  rfcMessageId?: string;
  /** The parts are an attached email's (ADR 0092), at this path. */
  attachedPath?: string;
  /** The folder asked for; undefined is the server's remembered/default one. */
  requestedFolder?: string;
  /** The folder the server saved into, from `start`. */
  folder?: string;
  folderUrl?: string;
  files: SaveFile[];
  state: 'queued' | 'running' | 'done' | 'cancelled' | 'failed';
  /** A fatal refusal (the link rejected, not linked, rate limited …). */
  problem?: string;
  problemCode?: string;
  /** The message moved meanwhile and was found again under this id. */
  movedTo?: string;
  share?: SaveShare;
  /** The snackbar was dismissed; the rows still show what happened. */
  dismissed?: boolean;
}

export interface SaveRequest {
  accountId: string;
  messageId: string;
  subject: string;
  rfcMessageId?: string;
  /** The parts are an attached email's (ADR 0092), at this path. */
  attachedPath?: string;
  /** Undefined: the server's remembered or default folder. */
  folder?: string;
  files: ReadonlyArray<{ partId: string; filename: string; sizeBytes: number }>;
}

export interface SaveDeps {
  api: MailApi;
  queryClient: QueryClient;
}

/** How many finished saves are kept for the rows to show. */
const KEEP = 12;

let jobs: readonly SaveJob[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const controllers = new Map<number, AbortController>();
/** How to tell the server a running save is cancelled. */
const cancellers = new Map<number, () => void>();
const waiting: Array<{ id: number; deps: SaveDeps }> = [];
let running: number | undefined;

function emit(): void {
  for (const listener of listeners) listener();
}

function patch(id: number, change: (job: SaveJob) => SaveJob): void {
  jobs = jobs.map((job) => (job.id === id ? change(job) : job));
  emit();
}

function patchFile(id: number, partId: string, change: (file: SaveFile) => SaveFile): void {
  patch(id, (job) => ({ ...job, files: job.files.map((f) => (f.partId === partId ? change(f) : f)) }));
}

export function subscribeSaves(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSaves(): readonly SaveJob[] {
  return jobs;
}

/** The job with this id, if it is still remembered. */
export function getSave(id: number): SaveJob | undefined {
  return jobs.find((job) => job.id === id);
}

/**
 * Every file of every remembered save of one message, the latest word per
 * part. An attached email's parts (ADR 0092) are their own set: part "2" of
 * the message and part "2" of an email attached to it are different files.
 */
export function filesFor(accountId: string, messageId: string, attachedPath?: string): ReadonlyMap<string, SaveFile> {
  const out = new Map<string, SaveFile>();
  for (const job of jobs) {
    if (job.accountId !== accountId || (job.messageId !== messageId && job.movedTo !== messageId)) continue;
    if (job.attachedPath !== attachedPath) continue;
    for (const file of job.files) out.set(file.partId, file);
  }
  return out;
}

/** A refusal in words, for one file or for the whole save. */
export function describeSaveProblem(error: ApiErrorBody | ApiError | unknown, name?: string): string {
  const body: ApiErrorBody | undefined =
    error instanceof ApiError
      ? { code: String(error.code ?? ''), message: error.message, ...(error.details ? { details: error.details } : {}) }
      : typeof error === 'object' && error !== null && 'code' in error
        ? (error as ApiErrorBody)
        : undefined;
  if (body === undefined) {
    return error instanceof Error && error.message !== '' ? error.message : 'Something went wrong.';
  }
  const what = name ?? 'the file';
  switch (body.code) {
    case ApiErrorCodes.NextcloudQuotaExceeded: {
      const free = Number(body.details?.free_bytes);
      return Number.isFinite(free)
        ? `There is not enough room for ${what} on your Nextcloud (${formatBytes(free)} free).`
        : `There is not enough room for ${what} on your Nextcloud.`;
    }
    case ApiErrorCodes.NextcloudUnreachable:
      return `Your Nextcloud did not answer, so ${what} was not saved. Try again.`;
    case ApiErrorCodes.NextcloudRejected:
      return 'Nextcloud no longer accepts the link. Link it again in Settings.';
    case ApiErrorCodes.NextcloudNotLinked:
      return 'Link a Nextcloud account in Settings first.';
    case ApiErrorCodes.RateLimited:
      return body.message !== '' ? body.message : 'That is a lot of saving at once. Wait a moment and try again.';
    default:
  }
  if (error instanceof ApiError && error.isOffline) return `${what} was not saved: you appear to be offline.`;
  return body.message !== '' ? body.message : `${what} could not be saved.`;
}

/** Codes after which the link's status has changed on the server. */
const LINK_CHANGED: ReadonlySet<string> = new Set([ApiErrorCodes.NextcloudRejected, ApiErrorCodes.NextcloudNotLinked]);

function isAbort(error: unknown): boolean {
  return (error instanceof DOMException && error.name === 'AbortError') || (error instanceof Error && error.name === 'AbortError');
}

/**
 * Asks for a save. Returns the job's id; the job runs now, or after the one
 * that is running.
 */
export function startSave(deps: SaveDeps, request: SaveRequest): number {
  seq += 1;
  const id = seq;
  const job: SaveJob = {
    id,
    saveId: newSaveId(),
    accountId: request.accountId,
    messageId: request.messageId,
    subject: request.subject,
    ...(request.rfcMessageId !== undefined && request.rfcMessageId !== '' ? { rfcMessageId: request.rfcMessageId } : {}),
    ...(request.attachedPath !== undefined ? { attachedPath: request.attachedPath } : {}),
    ...(request.folder !== undefined ? { requestedFolder: request.folder } : {}),
    files: request.files.map((f) => ({ ...f, state: 'queued', doneBytes: 0, totalBytes: f.sizeBytes })),
    state: 'queued',
  };
  // Oldest finished saves are forgotten first; a running or queued one never.
  const finished = jobs.filter((j) => j.state !== 'running' && j.state !== 'queued');
  const drop = new Set(finished.slice(0, Math.max(0, jobs.length + 1 - KEEP)).map((j) => j.id));
  jobs = [...jobs.filter((j) => !drop.has(j.id)), job];
  emit();
  waiting.push({ id, deps });
  pump();
  return id;
}

function pump(): void {
  if (running !== undefined) return;
  const next = waiting.shift();
  if (next === undefined) return;
  running = next.id;
  void run(next.id, next.deps).finally(() => {
    running = undefined;
    pump();
  });
}

async function run(id: number, { api, queryClient }: SaveDeps): Promise<void> {
  const job = getSave(id);
  if (job === undefined || job.state !== 'queued') return;
  const controller = new AbortController();
  controllers.set(id, controller);
  cancellers.set(id, () => void api.nextcloud.cancelNextcloudSave(job.saveId).catch(() => undefined));
  patch(id, (j) => ({ ...j, state: 'running' }));
  const nameOf = (partId: string): string =>
    getSave(id)?.files.find((f) => f.partId === partId)?.filename ?? 'the file';

  const onEvent = (event: NextcloudSaveEvent): void => {
    switch (event.type) {
      case 'start':
        patch(id, (j) => ({ ...j, folder: event.folder, folderUrl: event.folderUrl }));
        return;
      case 'moved':
        patch(id, (j) => ({ ...j, movedTo: event.messageId }));
        return;
      case 'progress':
        // A late byte count never undoes a file's outcome.
        patchFile(id, event.partId, (f) => f.state === 'saved' || f.state === 'failed' ? f : ({
          ...f,
          state: event.phase,
          doneBytes: event.doneBytes,
          totalBytes: event.totalBytes > 0 ? event.totalBytes : f.totalBytes,
        }));
        return;
      case 'saved':
        patchFile(id, event.partId, (f) => ({
          ...f,
          state: 'saved',
          doneBytes: event.sizeBytes,
          totalBytes: event.sizeBytes,
          name: event.name,
          path: event.path,
          openUrl: event.openUrl,
          renamed: event.renamed,
        }));
        return;
      case 'failed':
        patchFile(id, event.partId, (f) => ({
          ...f,
          state: 'failed',
          problem: describeSaveProblem(event.error, nameOf(event.partId)),
        }));
        return;
      case 'done':
        patch(id, (j) => ({ ...j, folder: event.folder, folderUrl: event.folderUrl }));
        return;
      default:
    }
  };

  try {
    await api.nextcloud.saveAttachments(
      job.accountId,
      job.messageId,
      {
        partIds: job.files.map((f) => f.partId),
        ...(job.requestedFolder !== undefined ? { folder: job.requestedFolder } : {}),
        ...(job.rfcMessageId !== undefined ? { rfcMessageId: job.rfcMessageId } : {}),
        ...(job.attachedPath !== undefined ? { attachedPath: job.attachedPath } : {}),
        saveId: job.saveId,
      },
      onEvent,
      controller.signal,
    );
    patch(id, (j) => ({
      ...j,
      state: 'done',
      // A part the server never reported on (it should not happen) is not
      // left spinning.
      files: j.files.map((f) =>
        f.state === 'saved' || f.state === 'failed' ? f : { ...f, state: 'failed', problem: `${f.filename} was not saved.` },
      ),
    }));
  } catch (error) {
    const cancelled = controller.signal.aborted || isAbort(error);
    const code = error instanceof ApiError ? String(error.code ?? '') : '';
    patch(id, (j) => ({
      ...j,
      state: cancelled ? 'cancelled' : 'failed',
      ...(cancelled ? {} : { problem: describeSaveProblem(error), problemCode: code }),
      files: j.files.map((f) =>
        f.state === 'saved' || f.state === 'failed'
          ? f
          : { ...f, state: cancelled ? 'cancelled' : 'failed', ...(cancelled ? {} : { problem: describeSaveProblem(error, f.filename) }) },
      ),
    }));
    if (LINK_CHANGED.has(code)) void queryClient.invalidateQueries({ queryKey: queryKeys.nextcloud() });
  } finally {
    controllers.delete(id);
    cancellers.delete(id);
  }
  const done = getSave(id);
  if (done?.files.some((f) => f.state === 'saved')) {
    // The remembered folder and the quota changed; a folder listing is stale.
    void queryClient.invalidateQueries({ queryKey: queryKeys.nextcloud() });
    void queryClient.invalidateQueries({ queryKey: ['nextcloud', 'files'] });
  }
}

/**
 * Stops a save: the part in flight is abandoned on the server; saved ones
 * stay. Said to the server by id as well as by dropping the stream -- a proxy
 * between us need not close the connection when the page stops reading it
 * (found live: the save ran to the end after Cancel).
 */
export function cancelSave(id: number): void {
  const at = waiting.findIndex((w) => w.id === id);
  if (at >= 0) {
    waiting.splice(at, 1);
    patch(id, (j) => ({ ...j, state: 'cancelled', files: j.files.map((f) => ({ ...f, state: 'cancelled' })) }));
    return;
  }
  cancellers.get(id)?.();
  controllers.get(id)?.abort();
}

/** A random id for one save, in the shape the server accepts. */
function newSaveId(): string {
  const bytes = new Uint8Array(12);
  globalThis.crypto.getRandomValues(bytes);
  return `save-${[...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * *Copy share link* on a single saved file: a link share with the person's
 * v1 settings (expiry, password policy), recorded under "Files shared from
 * mail" as `saved` -- the only way a saved file appears there. Then the
 * clipboard; when the browser refuses it, the link is shown instead.
 */
export async function copyShareLink(deps: SaveDeps, id: number): Promise<void> {
  const job = getSave(id);
  const file = job?.files.find((f) => f.state === 'saved');
  if (job === undefined || file?.path === undefined) return;
  if (job.share?.url !== undefined) {
    await writeClipboard(id, job.share.url, job.share.password);
    return;
  }
  patch(id, (j) => ({ ...j, share: { state: 'working' } }));
  try {
    const shared = await deps.api.nextcloud.shareNextcloudFile({
      path: file.path,
      savedFrom: { accountId: job.accountId, subject: job.subject, rfcMessageId: job.rfcMessageId ?? '' },
    });
    patch(id, (j) => ({
      ...j,
      share: { state: 'working', url: shared.shareUrl, ...(shared.password !== undefined ? { password: shared.password } : {}) },
    }));
    void deps.queryClient.invalidateQueries({ queryKey: queryKeys.nextcloudUploads() });
    await writeClipboard(id, shared.shareUrl, shared.password);
  } catch (error) {
    const code = error instanceof ApiError ? String(error.code ?? '') : '';
    if (LINK_CHANGED.has(code)) void deps.queryClient.invalidateQueries({ queryKey: queryKeys.nextcloud() });
    patch(id, (j) => ({ ...j, share: { state: 'failed', problem: describeSaveProblem(error, file.name) } }));
  }
}

async function writeClipboard(id: number, url: string, password: string | undefined): Promise<void> {
  try {
    if (navigator.clipboard === undefined) throw new Error('no clipboard');
    await navigator.clipboard.writeText(url);
    patch(id, (j) => ({ ...j, share: { state: 'copied', url, ...(password !== undefined ? { password } : {}) } }));
  } catch {
    patch(id, (j) => ({ ...j, share: { state: 'shown', url, ...(password !== undefined ? { password } : {}) } }));
  }
}

/** Forgets a finished save (the snackbar was dismissed); a running one is kept. */
export function dismissSave(id: number): void {
  const job = getSave(id);
  if (job === undefined || job.state === 'running' || job.state === 'queued') return;
  patch(id, (j) => ({ ...j, dismissed: true }));
}

/** Tests only: forget everything. */
export function resetSavesForTests(): void {
  for (const controller of controllers.values()) controller.abort();
  controllers.clear();
  cancellers.clear();
  waiting.length = 0;
  running = undefined;
  jobs = [];
  seq = 0;
  emit();
}
